import Button from 'tio-ui/components/button';
import { debounce } from '@ember/runloop';
import Popover from 'tio-ui/components/popover';
import { dropTask, all } from 'ember-concurrency';
import { fn } from '@ember/helper';
import { not, or } from 'tio-ui/utilities';
import { on } from '@ember/modifier';
import { VStack } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import ErrorMessages from 'tio-ui/components/error-messages';
import Footer from 'tio-ui/components/footer';
import Modal from 'tio-ui/components/modal';
import TuitionAssistanceFormsAcceptConditions from '../forms/accept-conditions';
import type ConditionModel from 'tio-data/models/condition';
import type ErrorsSignature from 'tio-common/types/errors';
import type Intl from 'ember-intl/services/intl';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TasProgramInstanceModel from 'tio-data/models/tas-program-instance';
import type AgreementModel from 'tio-data/models/agreement';
import { PhDotsThreeVertical } from 'ember-phosphor-icons';
import {
  transitionTasProgramInstanceRequest,
  type TasProgramInstanceTransitionEvent,
} from 'tio-common/requests/tas-program-instance';
import { buildTasEmployeeActor } from 'tio-common/utils/tas-actor';

export interface WithdrawCompleteSignature {
  Args: {
    programInstance: TasProgramInstanceModel;
    isHeldParticipant: boolean;
  };
  Element: HTMLDivElement;
}

export default class TuitionAssistanceProgramDetailsWithdrawCompleteComponent extends Component<WithdrawCompleteSignature> {
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;
  @service declare intl: Intl;

  @tracked hasSubmitted = false;
  @tracked didSubmitSuccessfully = false;
  @tracked submitError?: ErrorsSignature;
  @tracked acceptedConditions: ConditionModel[] = [];
  @tracked action: string | null = null;
  @tracked showModal = false;
  @tracked modalText = { header: '', body: '' };
  @tracked isMenuOpen = false;

  toggleMenu = () => {
    this.isMenuOpen = !this.isMenuOpen;
  };

  closeMenu = () => {
    this.isMenuOpen = false;
  };

  get conditionsForSubmit() {
    return (
      this.args.programInstance?.tasProgramTemplate?.programApproveWithdrawCompleteConditions || []
    );
  }

  get showConditionsForSubmit() {
    return this.conditionsForSubmit.length && !this.isHeldParticipant;
  }

  get hasAgreedAllConditions() {
    if (!this.conditionsForSubmit.length || this.isHeldParticipant) {
      return true;
    }
    return this.conditionsForSubmit.length === this.acceptedConditions.length;
  }

  get transitionEvent(): TasProgramInstanceTransitionEvent | undefined {
    const eventMap: Record<string, TasProgramInstanceTransitionEvent> = {
      withdraw: 'request-withdrawal-approval',
      complete: 'request-completion-approval',
    };
    return this.action ? eventMap[this.action as keyof typeof eventMap] : undefined;
  }

  get markProgramCompleteModalText() {
    return this.isHeldParticipant
      ? {
          header: this.intl.t(
            'tuition_assistance.program_details.mark_program_complete.held_participant.header'
          ),
          body: this.intl.t(
            'tuition_assistance.program_details.mark_program_complete.held_participant.body'
          ),
        }
      : {
          header: this.intl.t('tuition_assistance.program_details.mark_program_complete.header'),
          body: this.intl.t('tuition_assistance.program_details.mark_program_complete.body'),
        };
  }

  get withdrawProgramModalText() {
    return this.isHeldParticipant
      ? {
          header: this.intl.t(
            'tuition_assistance.program_details.withdraw_program.held_participant.header'
          ),
          body: this.intl.t(
            'tuition_assistance.program_details.withdraw_program.held_participant.body'
          ),
        }
      : {
          header: this.intl.t('tuition_assistance.program_details.withdraw_program.header'),
          body: this.intl.t('tuition_assistance.program_details.withdraw_program.body'),
        };
  }

  get isHeldParticipant() {
    return this.args.isHeldParticipant;
  }

  async markProgramComplete() {
    if (!this.isHeldParticipant) {
      await this.submitTask.perform();
    }
  }

  async withdrawProgram() {
    if (!this.isHeldParticipant) {
      await this.submitTask.perform();
    }
  }

  didUpdateAcceptedConditions = (conditions: ConditionModel[] = []) => {
    this.acceptedConditions = conditions;
  };

  handleCompleteWithdrawAction = () => {
    switch (this.action) {
      case 'complete':
        this.markProgramComplete();
        break;
      case 'withdraw':
        this.withdrawProgram();
        break;
      default:
        break;
    }
  };

  submitTask = dropTask(async () => {
    if (!this.hasAgreedAllConditions) {
      return;
    }
    const event = this.transitionEvent;
    if (!event) return;
    try {
      this.hasSubmitted = true;
      delete this.submitError;
      await this.saveAgreementsForConditions.perform();
      await this.store.request(
        transitionTasProgramInstanceRequest(
          this.args.programInstance.id as string,
          event,
          buildTasEmployeeActor(this.sessionContext.user, this.sessionContext.currentRole)
        )
      );
      await this.args.programInstance.reload();
      this.didSubmitSuccessfully = true;
      this.closeModal();
    } catch (e) {
      console.error(e);
      this.submitError = e;
    }
  });

  saveAgreementsForConditions = dropTask(async () => {
    const user = this.sessionContext.user;
    const agreements = this.acceptedConditions.map((condition) => {
      return this.store.createRecord<AgreementModel>('agreement', {
        user: user,
        condition: condition,
      });
    });
    const promises = agreements.map((agreement) => {
      return agreement.save();
    });
    const saved = await all(promises);
    return saved;
  });

  closeModal = () => {
    this.showModal = false;
    this.action = null;
    this.submitError = undefined;
    this.hasSubmitted = false;
    this.acceptedConditions = [];
  };

  setShowModal = () => {
    this.showModal = true;
  };

  openModal = (value: string) => {
    this.isMenuOpen = false;
    this.action = value;
    this.modalText =
      value === 'complete' ? this.markProgramCompleteModalText : this.withdrawProgramModalText;
    debounce(this.setShowModal, 300);
  };

  <template>
    {{#if (or @programInstance.canMarkComplete @programInstance.canWithdraw)}}
      <div ...attributes>
        <div class="tio-flex-loose lg:hidden">
          <Popover
            @placement="bottom-end"
            @isOpen={{this.isMenuOpen}}
            @onClose={{this.closeMenu}}
            @maxWidth={{240}}
          >
            <:trigger as |p|>
              <button
                type="button"
                class="p-1 rounded border tio-border-default hover:tio-bg-sunken"
                {{p.register}}
                {{on "click" this.toggleMenu}}
                data-test-complete-menu
                aria-label={{t "tuition_assistance.program_details.program_actions_menu"}}
                aria-haspopup="menu"
                aria-expanded={{this.isMenuOpen}}
              >
                <PhDotsThreeVertical @size="24" />
              </button>
            </:trigger>
            <:body>
              <div class="tio-col-flush py-1">
                {{#if @programInstance.canMarkComplete}}
                  <button
                    type="button"
                    class="w-full px-3 py-2 text-sm text-left tio-fg-info hover:tio-bg-sunken rounded"
                    {{on "click" (fn this.openModal "complete")}}
                    data-test="complete-button"
                  >
                    {{t "tuition_assistance.program_details.mark_program_complete.default"}}
                  </button>
                {{/if}}
                {{#if @programInstance.canWithdraw}}
                  <button
                    type="button"
                    class="w-full px-3 py-2 text-sm text-left tio-fg-danger hover:tio-bg-sunken rounded"
                    {{on "click" (fn this.openModal "withdraw")}}
                    data-test="withdraw-button"
                  >
                    {{t "tuition_assistance.program_details.withdraw_program.default"}}
                  </button>
                {{/if}}
              </div>
            </:body>
          </Popover>
        </div>

        <div class="hidden lg:flex gap-4">
          {{#if @programInstance.canMarkComplete}}
            <Button
              @size="sm"
              @appearance="outlined"
              @intent="primary"
              {{on "click" (fn this.openModal "complete")}}
            >
              {{t "tuition_assistance.program_details.mark_program_complete.default"}}
            </Button>
          {{/if}}
          {{#if @programInstance.canWithdraw}}
            <Button
              @size="sm"
              @appearance="outlined"
              @intent="danger"
              {{on "click" (fn this.openModal "withdraw")}}
            >
              {{t "tuition_assistance.program_details.withdraw_program.default"}}
            </Button>
          {{/if}}
        </div>
      </div>
    {{/if}}

    <Modal @isOpen={{this.showModal}} @onClose={{this.closeModal}}>
      <:header>{{this.modalText.header}}</:header>
      <:body>
        <VStack>
          <p>{{this.modalText.body}}</p>
          {{#if this.showConditionsForSubmit}}
            <TuitionAssistanceFormsAcceptConditions
              @conditions={{this.conditionsForSubmit}}
              @onChange={{this.didUpdateAcceptedConditions}}
            />
          {{/if}}
          <ErrorMessages @showErrors={{this.hasSubmitted}} @error={{this.submitError}} />
        </VStack>
      </:body>
      <:footer>
        {{#if this.isHeldParticipant}}
          <Footer @onSubmit={{this.closeModal}} @submitText="Close" />
        {{else}}
          <Footer
            @onSubmit={{this.handleCompleteWithdrawAction}}
            @onCancel={{this.closeModal}}
            @submitText="Confirm"
            @submitDisabled={{not this.hasAgreedAllConditions}}
            @isRunning={{this.submitTask.isRunning}}
          />
        {{/if}}
      </:footer>
    </Modal>
  </template>
}
